/**
 * The words the settings page uses for the values it stores, and the lists those values come from.
 *
 * The campaign list and the workspace form both name the same enums, and a label that drifted
 * between them would describe one stored value as two different things on one screen.
 *
 * Each enum is an array first and a label record second. The array is what a `<select>` is narrowed
 * against when the browser hands back a string: `KINDS.find(...)` returns the union member or
 * nothing, which is a real narrowing rather than an assertion that the string must be one.
 */

/** Every booking mode a stored setting can hold, so each one has words. */
const CALENDAR_MODES = ["booking_link_only", "connector", "manual"] as const;
export type CalendarMode = (typeof CALENDAR_MODES)[number];
/**
 * The booking modes a rep may choose: a booking link or a connected calendar. `manual`, availability
 * the app kept itself, is retired (Dawid, 2026-09-30: "its either link or connector"); a workspace
 * or campaign still stored that way is shown as not set up until someone chooses.
 */
export const OFFERED_CALENDAR_MODES = [
  "booking_link_only",
  "connector",
] as const satisfies readonly CalendarMode[];
export type OfferedCalendarMode = (typeof OFFERED_CALENDAR_MODES)[number];
export const CALENDAR_LABEL: Readonly<Record<CalendarMode, string>> = {
  booking_link_only: "Booking link",
  connector: "Connected calendar",
  manual: "Not set up",
};
export const CALENDAR_HINT: Readonly<Record<OfferedCalendarMode, string>> = {
  booking_link_only: "Drafts share the link below and let the lead pick a time.",
  connector:
    "Bookings are confirmed from the connected calendar. Drafts share the booking page below.",
};

export const ADMISSION_POLICIES = ["all_replies", "qualified_leads_only"] as const;
export type AdmissionPolicy = (typeof ADMISSION_POLICIES)[number];
export const ADMISSION_LABEL: Readonly<Record<AdmissionPolicy, string>> = {
  all_replies: "Every reply",
  qualified_leads_only: "Qualified leads only",
};
export const ADMISSION_HINT: Readonly<Record<AdmissionPolicy, string>> = {
  all_replies: "Every reply that arrives gets a draft.",
  qualified_leads_only: "A reply waits for an explicit qualification decision before drafting.",
};

export const GOAL_KINDS = [
  "meeting",
  "event_participation",
  "commercial_exploration",
  "contribution_research",
  "resource_delivery",
  "phone_call",
] as const;
export type GoalKind = (typeof GOAL_KINDS)[number];
export const GOAL_LABEL: Readonly<Record<GoalKind, string>> = {
  meeting: "Book a meeting",
  event_participation: "Register for an event",
  commercial_exploration: "Explore working together",
  contribution_research: "Gather a contribution",
  resource_delivery: "Send a resource",
  phone_call: "Get agreement to a phone call",
};

export const PARTICIPANT_ROLES = ["attendee", "speaker", "commercial_partner"] as const;
export type ParticipantRole = (typeof PARTICIPANT_ROLES)[number];
export const ROLE_LABEL: Readonly<Record<ParticipantRole, string>> = {
  attendee: "Attendee",
  speaker: "Speaker",
  commercial_partner: "Commercial partner",
};

export const OWNERS = ["agency", "client"] as const;
export type Owner = (typeof OWNERS)[number];
export const OWNER_LABEL: Readonly<Record<Owner, string>> = {
  agency: "This agency",
  client: "The client",
};

export const CAMPAIGN_ACTIONS = [
  "acknowledge",
  "answer",
  "request_clarification",
  "share_resource",
  "qualify",
  "offer_meeting",
  "share_booking_link",
  "manage_booking",
  "share_registration_link",
  "collect_contribution",
  "collect_phone_call",
  "handoff",
] as const;
export type CampaignAction = (typeof CAMPAIGN_ACTIONS)[number];
export const ACTION_LABEL: Readonly<Record<CampaignAction, string>> = {
  acknowledge: "Acknowledge the reply",
  answer: "Answer a question",
  request_clarification: "Ask for clarification",
  share_resource: "Share a resource",
  qualify: "Qualify the lead",
  offer_meeting: "Offer a meeting",
  share_booking_link: "Share the booking link",
  manage_booking: "Change or cancel a booking",
  share_registration_link: "Share a registration link",
  collect_contribution: "Collect a contribution",
  handoff: "Hand the conversation over",
  collect_phone_call: "Ask for call agreement or phone details",
};

/**
 * The two a policy must always permit. `nextStepPolicySchema` refuses a policy without them, so the
 * form shows them as permanently on rather than offering a choice that would be refused on save.
 */
export const ALWAYS_PERMITTED = ["acknowledge", "request_clarification"] as const;
export const OPTIONAL_ACTIONS: readonly CampaignAction[] = CAMPAIGN_ACTIONS.filter(
  (action) => !ALWAYS_PERMITTED.some((always) => always === action),
);

const ALLOWED_ACTIONS = ["meeting_create", "unsubscribe", "block"] as const;
/*
 * What the settings offer to switch on. Unsubscribing or blocking a lead is Email Bison's job, never
 * this app's (AGENTS.md, Dawid 2026-09-28): the two stay storable so an older configuration still
 * parses, but nothing offers them and the projection no longer turns a stop into one.
 */
export const OFFERED_ACTIONS: readonly AllowedAction[] = ["meeting_create"];
export type AllowedAction = (typeof ALLOWED_ACTIONS)[number];
export const ALLOWED_ACTION_LABEL: Readonly<Record<AllowedAction, string>> = {
  meeting_create: "Create the calendar event and send its invitation",
  unsubscribe: "Unsubscribe the lead in Bison",
  block: "Block the lead in Bison",
};

/** What a campaign may do, in words. A stored unsubscribe or block is inert and is not named. */
export function offeredActionsLabel(actions: readonly AllowedAction[]): string {
  const offered = actions.filter((action) => OFFERED_ACTIONS.includes(action));
  return offered.length === 0
    ? "Nothing"
    : offered.map((action) => ALLOWED_ACTION_LABEL[action]).join(", ");
}

export const SIGN_OFF_MODES = ["first_name", "none", "full_signature"] as const;
export type SignOffMode = (typeof SIGN_OFF_MODES)[number];
export const SIGN_OFF_LABEL: Readonly<Record<SignOffMode, string>> = {
  first_name: "“Best,” and the sender's first name",
  none: "Nothing",
  full_signature: "The mailbox's full signature",
};

/**
 * The option a `<select>` handed back, narrowed against the options themselves.
 *
 * `event.target.value` is a `string`, and the alternative to this is asserting it into the union,
 * which the convention sheet refuses: a value that is not one of the options — a stale page, a
 * hand-edited DOM — would then be stored as if it were. Here it is simply not found, and the caller
 * keeps what it had.
 */
export const chosen = <T extends string>(value: string, options: readonly T[]): T | undefined =>
  options.find((option) => option === value);
